import React, { useCallback, useMemo, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { TbArrowLeft } from 'react-icons/tb';

import { createLocale } from 'locales';

import type { CrumbType } from 'components-new/breadcrumbs/crumb';
import Breadcrumbs from 'components-new/breadcrumbs/breadcrumbs';
import Drawer from 'components-new/drawer/drawer';
import NavigationBar from 'components-new/navigation-bar/navigation-bar';
import TextButton from 'components-new/text-button/text-button';
import Typography from 'components-new/typography/typography';

import getNewNavigationBarLinks, {
  getAccountMenuItems,
  getAccountMenuViews,
} from 'utils/get-new-navigation-bar-links';
import getSidebarLinks from 'utils/get-sidebar-links';

import { useOrganisationContext } from 'state/context/OrganisationContext';

import type { OrganisationType } from 'pages/organization/components/organisation-select/organisation-select';

import OrganisationSideBar from '../organisation-side-bar';
import SideBarHeader from '../organisation-side-bar/SideBarHeader';
import SideBarElement from '../organisation-side-bar/SideBarElement';

const localize = createLocale('organisationIntegrationSettingsLocales');

interface OrganisationSettingsPageContainerProps {
  children: React.ReactNode;
  title: string;
  crumbs?: CrumbType[];
  containerSize?: 'full' | 'medium';
  selectedRoute?: {
    groupName: string;
    name: string;
  };
  actionElement?: React.ReactElement | false;
}

const OrganisationSettingsPageContainer: React.FC<
  OrganisationSettingsPageContainerProps
> = ({
  children,
  title,
  crumbs,
  containerSize = 'medium',
  selectedRoute,
  actionElement,
}) => {
  const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);

  const {
    user: { data },
    organisations: {
      organisations: organisationAdminData,
      selectedOrganisationId,
      setSelectedOrganisationId,
    },
  } = useOrganisationContext();

  const links = getNewNavigationBarLinks('settings');

  const accountMenuItems = getAccountMenuItems(data?.is_freelancer || false);

  const accountMenuViews = getAccountMenuViews(
    data?.is_freelancer || false,
    data?.is_organisation_admin || false,
    data?.is_staff || false,
    'organisation',
  );

  const sideBarLinks = () => {
    const selectedLink = selectedRoute;
    return getSidebarLinks(selectedLink);
  };

  const onOrganisationClick = useCallback(
    (id: number | undefined) => {
      setSelectedOrganisationId(id);
    },
    [setSelectedOrganisationId],
  );

  const organisations: OrganisationType[] | undefined = useMemo(() => {
    return organisationAdminData?.map((org) => ({
      id: org.id as number,
      image: org.logo as string | undefined,
      name: org.company_name,
      onClick: () => onOrganisationClick(org.id),
      selected: org.id === selectedOrganisationId,
    }));
  }, [onOrganisationClick, organisationAdminData, selectedOrganisationId]);

  const pageContainerClassName = twMerge(
    'm-5 md:m-12 flex-1',
    containerSize === 'medium' && 'max-w-2xl',
  );

  const sidebarLinks = sideBarLinks();

  const sidebarContent =
    sidebarLinks.length &&
    sidebarLinks.map((routeGroup) => (
      <ul key={routeGroup.name} className="my-10">
        <SideBarHeader label={routeGroup.label} />
        {routeGroup.routes.map((route) => (
          <SideBarElement key={route.id} route={route} />
        ))}
      </ul>
    ));

  return (
    <div>
      <NavigationBar
        variant="neutral"
        className="col-span-2 w-full"
        user={data}
        links={links}
        accountMenuItems={accountMenuItems}
        accountMenuViews={accountMenuViews}
        selectedOrganisation={organisations?.find((org) => org.selected)}
        organisations={organisations}
      />

      <div className="relative md:flex h-[calc(100vh-65px)]">
        {!selectedOrganisationId ? (
          <div className="flex items-center justify-center text-center w-full">
            <Typography variant="heading-1" as="h1" className="text-center">
              {localize('organisationNotSelectedError')}
            </Typography>
          </div>
        ) : (
          <>
            <Drawer
              isOpen={drawerIsOpen}
              onClose={() => setDrawerIsOpen(false)}
              drawerClassName="block md:hidden sm:w-[100vw] md:w-64"
              containerClassName="md:hidden"
              backdrop={false}
            >
              {sidebarContent}
            </Drawer>
            <OrganisationSideBar content={sidebarContent} />
            <div
              className={twMerge(
                'w-full overflow-y-scroll',
                containerSize !== 'full' && 'flex justify-center',
              )}
            >
              <div className={pageContainerClassName}>
                <div className="border-b-2 border-gray100">
                  {!drawerIsOpen && (
                    <TextButton
                      className="md:hidden"
                      variant="primary"
                      label={localize('backToSettings')}
                      onClick={() => setDrawerIsOpen((prev) => !prev)}
                      leftIconProps={{ icon: TbArrowLeft }}
                    />
                  )}
                  <div className="mb-2">
                    {crumbs && <Breadcrumbs crumbs={crumbs} />}
                  </div>
                  <div className="flex">
                    <Typography
                      as="h1"
                      variant="heading-1"
                      className="capitalize flex-1 mb-3"
                    >
                      {title}
                    </Typography>
                    {actionElement}
                  </div>
                </div>
                <div className="my-5">{children}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrganisationSettingsPageContainer;
